import { EdMarketPlaceSurveyService } from './modules/ed-market-place-survey/ed-market-place-survey.service';
import { Component, HostListener, Inject, OnDestroy, OnInit, Renderer2, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import {
	ActiveProductService,
	AuthenticationService,
	HybridAuthService,
	LiveChatService,
	Participant,
	ParticipantService
} from '@edassist/api';
import {
	AppConfigService,
	AppTitleService,
	constants,
	FEATURE_FLAGS,
	LiveChatUtilityService,
	LocalStorageService,
	ParticipantUtilityService,
	PendoService,
	ScriptLoadingService,
	WINDOW,
	ClientService,
	CLIENT_FEATURE_REFERENCE_CODES,
	LodashUtilityService,
	GoogleTagManagerEventService
} from '@edassist/common';
import { BehaviorSubject, forkJoin, Observable, Subscriber, Subscription } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { switchMap } from 'rxjs/operators';
import { GrowthBookService } from './core/services/growth-book/growthbook.service';

@Component({
	selector: 'tu-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
	constants = constants;
	siteRedesignEnabled = false;
	isNotificationsEnabled = false;
	$participantData: Observable<Participant>;
	private readonly participantSubject: BehaviorSubject<Participant>;
	private readonly renderer: Renderer2;
	private firstTabPress: boolean;
	private isMousePress: boolean;
	isWelcomeRoute: boolean = true;
	isMarketPlaceRoute = false;
	participantId: number;
	showEdMarketPlaceSurveyModal: boolean;
	private readonly subscription: Subscription = new Subscription();

	constructor(
		@Inject(WINDOW) protected readonly window: Window,
		private readonly hybridAuthService: HybridAuthService,
		private readonly authService: AuthenticationService,
		private readonly participantService: ParticipantService,
		private readonly pendoService: PendoService,
		private readonly liveChatService: LiveChatService,
		private readonly liveChatUtilityService: LiveChatUtilityService,
		private readonly activeProductService: ActiveProductService,
		private readonly appTitleService: AppTitleService,
		private readonly rendererFactory2: RendererFactory2,
		private readonly scriptLoadingService: ScriptLoadingService,
		private readonly cookieService: CookieService,
		private readonly clientService: ClientService,
		private readonly router: Router,
		private readonly gtmEventService: GoogleTagManagerEventService,
		private readonly growthBookService: GrowthBookService,
		private readonly edMarketPlaceService: EdMarketPlaceSurveyService
	) {
		this.participantSubject = new BehaviorSubject<Participant>(undefined);
		this.$participantData = this.participantSubject.asObservable();
		// tslint:disable-next-line:no-null-keyword
		this.renderer = this.rendererFactory2.createRenderer(null, null);
	}

	ngOnInit(): void {
		this.router?.events?.subscribe((navigation) => {
			// This is to handle skip to main content from index.html
			if (
				navigation instanceof NavigationEnd ||
				(navigation instanceof Scroll && navigation.routerEvent instanceof NavigationEnd)
			) {
				this.firstTabPress = true;
				this.isMousePress = false;
				this.isWelcomeRoute = this.router.url === this.constants.routes.welcome;

				// Push page_view event when navigation ends
				const userId = ParticipantUtilityService.getConsolidatedLoginUserId();
				this.participantId = this.authService?.currentUserValue?.participantId;
				const clientName = ParticipantUtilityService.getClientLoginDetails()?.clientName;
				const crmClientId = ParticipantUtilityService.getClientLoginDetails()?.crmClientId?.toString();
				// Send page view event to GTM
				this.gtmEventService.sendPageView(userId, this.participantId?.toString(), clientName, undefined, crmClientId);
			}

			if (
				navigation instanceof NavigationEnd &&
				navigation.url.includes(constants.routes.marketplace) &&
				this.authService.isLoggedIn
			) {
				this.edMarketPlaceService.handleShowExplorationSurveyModal();
			}
		});
		// Baseline User Session Data from base app
		this.hybridAuthService.handleHybridUser();
		this.isNotificationsEnabled = AppConfigService.isFeatureFlagEnabled(FEATURE_FLAGS.NOTIFICATIONS_ENABLED);
		if (AppConfigService.isFeatureFlagEnabled(FEATURE_FLAGS.ONE_TRUST_COOKIE_BANNER_ENABLED)) {
			// Initialize OneTrust Cookie Consent Banner
			this.initializeCookieConsentBanner();
		} else {
			// If OneTrust is disabled, then continue to work as before, i.e., start Pendo by default.
			this.startAnalytics(undefined);
			this.startChatLingual(undefined);
		}

		this.appTitleService.setPageTitles();
		const clientDetails = ParticipantUtilityService.getClientLoginDetails();
		this.siteRedesignEnabled = this.clientService.isClientFeatureEnabled(CLIENT_FEATURE_REFERENCE_CODES.SITE_REDESIGN);
		if (clientDetails.clientId !== constants.ADMIN_CLIENT_ID && this.authService.currentUserValue?.participantId) {
			forkJoin([this.participantService.getParticipant(this.authService.currentUserValue.participantId)]).subscribe(
				([participant]) => {
					this.participantService.setParticipantBehaviorSubject(participant);
				}
			);
		}

		this.subscription.add(
			this.edMarketPlaceService.showExplorationSurveyEvent.subscribe((showSurvey: boolean) => {
				this.showEdMarketPlaceSurveyModal = showSurvey;
			})
		);

		this.growthBookService.initializeGrowthBook({
			edaClientId: ParticipantUtilityService.getClientLoginDetails()?.clientId,
			edaProductTypeId: this.activeProductService.getActiveProduct().id
		});
	}

	initializeCookieConsentBanner(): void {
		const oneTrustDataDomainScript = AppConfigService.getSettings().oneTrust.dataDomainScript;

		// Load the OneTrust cookie consent script as a first step as part of the app bootstrap.
		this.scriptLoadingService.loadJsScript(
			this.renderer,
			'https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js',
			undefined,
			'one-trust-script',
			false,
			oneTrustDataDomainScript
		);
	}

	// So we can get a fresh token from the base app everytime to avoid tokens getting mismatched
	// Can be removed once hybrid is no longer being used. The Destroy event is not always being triggered, so
	// listen to when actual window is unloaded (DOM event)
	@HostListener('window:beforeunload')
	clearUserFromStorage(): void {
		LocalStorageService.removeItem(constants.localStorageKeys.CURRENT_USER);
		LocalStorageService.removeItem(constants.localStorageKeys.SESSION_TOKEN);
		LocalStorageService.removeItem(constants.localStorageKeys.REFRESH_TOKEN);
		this.unsetOneTrustRelatedInitializationKeys();
	}

	// Taking advantage of the "window:beforeunload" event before navigating away from the Angular 12+ site to the AngularJS site
	// to execute the cookie banner-specific un-initialization action in session storage. By doing this, we set a suitable starting point
	// for the "edassist-web" bootstrapping controller to allow initializing the cookie banner-related session keys unambiguously.
	unsetOneTrustRelatedInitializationKeys(): void {
		sessionStorage.setItem(constants.sessionStorageKeys.CHAT_LINGUAL_INITIALIZED, 'false');
		sessionStorage.setItem(constants.sessionStorageKeys.WEB_ANALYTICS_INITIALIZED, 'false');
		sessionStorage.setItem(constants.sessionStorageKeys.ONE_TRUST_ACTIVE_GROUPS, undefined);
	}

	@HostListener('window:cookieConsentEvent')
	handleOneTrustPreferences(): void {
		// tslint:disable:no-string-literal
		const currentOTActiveGroups = document.defaultView['OnetrustActiveGroups'].replace(/^,|,$/g, ''); // to get rid of start and end commas
		const existingOTActiveGroups = sessionStorage.getItem(constants.sessionStorageKeys.ONE_TRUST_ACTIVE_GROUPS);
		const isWebAnalyticsInitialized = sessionStorage.getItem(constants.sessionStorageKeys.WEB_ANALYTICS_INITIALIZED);
		const isChatLingualInitialized = sessionStorage.getItem(constants.sessionStorageKeys.CHAT_LINGUAL_INITIALIZED);

		if (this.isCookieGroupsModified(existingOTActiveGroups, currentOTActiveGroups)) {
			const currentOTActiveGroupsArray = currentOTActiveGroups.split(',');
			sessionStorage.setItem(constants.sessionStorageKeys.ONE_TRUST_ACTIVE_GROUPS, currentOTActiveGroups);

			this.processPerformanceAndTargetingCookiePreferences(currentOTActiveGroupsArray)
				.pipe(switchMap((complete) => this.processFunctionalCookiePreferences(currentOTActiveGroupsArray)))
				.subscribe((result) => {
					// Check if reload needed
					const isWindowReloadNeeded = sessionStorage.getItem(constants.sessionStorageKeys.WINDOW_RELOAD_NEEDED);
					if (isWindowReloadNeeded === 'true') {
						sessionStorage.setItem(constants.sessionStorageKeys.WINDOW_RELOAD_NEEDED, 'false');
						window.location.reload();
					}
				});
		} else if (!this.isCookieGroupsModified(existingOTActiveGroups, currentOTActiveGroups)) {
			if (isWebAnalyticsInitialized === 'true') {
				this.startAnalytics(undefined);
			}
			if (isChatLingualInitialized === 'true') {
				this.startChatLingual(undefined);
			}
		}
	}

	@HostListener('window:scroll', ['$event'])
	@HostListener('document:keydown', ['$event'])
	handleKeyDown(event: KeyboardEvent): void {
		// If key press is only tab key and on first route change
		if (event.key === 'Tab') {
			if (this.firstTabPress && !this.isMousePress) {
				// set Focus on skipToMainContcd ent and reset back the firstTabPress to false
				const skipToMainContent = document.getElementById('skipToMainContent');
				setTimeout(() => {
					skipToMainContent?.focus();
				});
				this.firstTabPress = false;
			}
		}
	}

	@HostListener('document:click', ['$event'])
	handleMouseClick(event: MouseEvent): void {
		this.isMousePress = true;
	}

	startAnalytics(subscriber: Subscriber<boolean>): void {
		// Ensure the OneTrust script loads completely first. Then load the analytics scripts such as Pendo, Google Analytics, and GTM.
		const script = this.scriptLoadingService.loadJsScript(
			this.renderer,
			'pendo.js',
			undefined,
			'analytics',
			false,
			undefined
		);
		script.onload = () => {
			this.applyAnalyticsPreferences(true);
			sessionStorage.setItem(constants.sessionStorageKeys.WEB_ANALYTICS_INITIALIZED, 'true');
			if (subscriber) {
				subscriber.next(true);
			}
		};
	}

	stopAnalytics(subscriber: Subscriber<boolean>): void {
		sessionStorage.setItem(constants.sessionStorageKeys.WEB_ANALYTICS_INITIALIZED, 'false');
		this.applyAnalyticsPreferences(false);
		sessionStorage.setItem(constants.sessionStorageKeys.WINDOW_RELOAD_NEEDED, 'true');
		if (subscriber) {
			subscriber.next(true);
		}
	}

	startChatLingual(subscriber: Subscriber<boolean>): void {
		sessionStorage.setItem(constants.sessionStorageKeys.FUNCTIONAL_COOKIES_OPTED_IN, 'true');
		// Reset CHAT_LINGUAL_INITIALIZED to false so that loadChatBox will reinitialize it
		sessionStorage.setItem(constants.sessionStorageKeys.CHAT_LINGUAL_INITIALIZED, 'false');
		if (
			this.authService.isLoggedIn &&
			LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS)?.liveChatEnabled
		) {
			// initialize chat-lingual
			this.liveChatService.getAllLiveChatConfigs().subscribe((response) => {
				// Loading chat box script on page load
				this.liveChatUtilityService.loadChatBox(
					this.activeProductService.getActiveProduct().id,
					response,
					this.authService.currentUserValue
				);
			});
		}
		if (subscriber) {
			subscriber.next(true);
		}
	}

	stopChatLingual(subscriber: Subscriber<boolean>): void {
		if (
			LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS)?.liveChatEnabled &&
			sessionStorage.getItem(constants.sessionStorageKeys.FUNCTIONAL_COOKIES_OPTED_IN) === 'true'
		) {
			// stop chat-lingual
			this.liveChatUtilityService.removeChatBox();
			sessionStorage.setItem(constants.sessionStorageKeys.WINDOW_RELOAD_NEEDED, 'true');
		}
		sessionStorage.setItem(constants.sessionStorageKeys.FUNCTIONAL_COOKIES_OPTED_IN, 'false');
		if (subscriber) {
			subscriber.next(true);
		}
	}

	isCookieGroupsModified(existingCookieGroups: string, currentCookieGroups: string): boolean {
		if (!existingCookieGroups) {
			return true;
		}
		const group1Array = existingCookieGroups.split(',').sort();
		const group2Array = currentCookieGroups.split(',').sort();

		return (
			group1Array.length !== group2Array.length || !group1Array.every((value, index) => value === group2Array[index])
		);
	}

	applyAnalyticsPreferences(webAnalyticsEnabled: boolean): void {
		if (webAnalyticsEnabled) {
			const clientDetails =
				ParticipantUtilityService.getClientLoginDetails() ||
				LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS);
			if (clientDetails.clientId !== constants.ADMIN_CLIENT_ID && this.authService.currentUserValue?.participantId) {
				this.participantService
					.getParticipant(this.authService.currentUserValue.participantId)
					.subscribe((participant) => {
						// TODO: Move this to login when login has been moved to new codebase
						this.pendoService.initialize(participant, clientDetails);
					});
			} else {
				this.pendoService.initialize(undefined, clientDetails);
			}
		} else {
			// Force a flush of cached event objects.
			this.pendoService.flushNow();
			const cookies = this.cookieService.getAll();
			LodashUtilityService.forOwn(cookies, (value, key) => {
				if (!key.toLowerCase().includes(constants.ONE_TRUST_COOKIE_PROPERTIES.OPTANON)) {
					this.cookieService.delete(
						key,
						constants.ONE_TRUST_COOKIE_PROPERTIES.DOMAIN_PATH,
						constants.ONE_TRUST_COOKIE_PROPERTIES.DOMAIN
					);
				}
			});

			// The following cookie removal is based on feedback from the privacy team.
			// In Phase 2 work, we will modularize this removal based on the cookie category.
			const clientDomain = ['.', window.location.hostname].join('');
			this.cookieService.delete(
				constants.COOKIES.PIN_UNAUTH,
				constants.ONE_TRUST_COOKIE_PROPERTIES.DOMAIN_PATH,
				clientDomain
			);
		}
	}

	processPerformanceAndTargetingCookiePreferences(activeGroups: Array<string>): Observable<boolean> {
		return new Observable((observer) => {
			const isWebAnalyticsInitialized = sessionStorage.getItem(constants.sessionStorageKeys.WEB_ANALYTICS_INITIALIZED);

			if (
				activeGroups.includes(constants.ONE_TRUST_COOKIE_TYPES.PERFORMANCE_COOKIES) &&
				activeGroups.includes(constants.ONE_TRUST_COOKIE_TYPES.TARGETING_COOKIES) &&
				(isWebAnalyticsInitialized === 'false' || !isWebAnalyticsInitialized)
			) {
				this.startAnalytics(observer);
			} else if (
				(!activeGroups.includes(constants.ONE_TRUST_COOKIE_TYPES.PERFORMANCE_COOKIES) &&
					isWebAnalyticsInitialized === 'true') ||
				(!activeGroups.includes(constants.ONE_TRUST_COOKIE_TYPES.TARGETING_COOKIES) &&
					isWebAnalyticsInitialized === 'true')
			) {
				this.stopAnalytics(observer);
			} else {
				observer.next(true);
			}
		});
	}

	processFunctionalCookiePreferences(activeGroups: Array<string>): Observable<boolean> {
		return new Observable((observer) => {
			const isChatLingualInitialized = sessionStorage.getItem(constants.sessionStorageKeys.CHAT_LINGUAL_INITIALIZED);

			if (
				activeGroups.includes(constants.ONE_TRUST_COOKIE_TYPES.FUNCTIONAL_COOKIES) &&
				(isChatLingualInitialized === 'false' || !isChatLingualInitialized)
			) {
				this.startChatLingual(observer);
			} else if (
				!activeGroups.includes(constants.ONE_TRUST_COOKIE_TYPES.FUNCTIONAL_COOKIES) &&
				isChatLingualInitialized === 'true'
			) {
				this.stopChatLingual(observer);
			} else {
				observer.next(true);
			}
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
